import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['destinationUrl', 'title', 'error'];

  connect() {
    console.log('QR Code Create Controller connected');

    // Create error container on connect to avoid missing target errors
    this.createErrorContainer();

    // Add validation for the form
    this.destinationUrlTarget.addEventListener(
      'blur',
      this.validateAndFetchTitle.bind(this)
    );
  }

  disconnect() {
    this.destinationUrlTarget.removeEventListener(
      'blur',
      this.validateAndFetchTitle.bind(this)
    );
  }

  // Create the error container when controller connects
  createErrorContainer() {
    if (!this.hasErrorTarget) {
      const errorDiv = document.createElement('div');
      errorDiv.classList.add('text-error', 'text-sm', 'mt-1');
      errorDiv.setAttribute('data-qr-code--create-target', 'error');
      errorDiv.style.minHeight = '20px'; // Keep some space so layout doesn't jump

      // Insert after the destination URL input
      this.destinationUrlTarget.insertAdjacentElement('afterend', errorDiv);
    }
  }

  validateAndFetchTitle(event) {
    const url = this.destinationUrlTarget.value.trim();

    if (!url) return;

    // Clear previous error
    this.clearError();

    const valid = this.isValidUrl(url);

    if (!valid) {
      this.showError('We\'ll need a valid URL, like "domain.co/hello"');
      return;
    }

    if (valid) {
      // Get the button spinner controller
      const buttonSpinnerController =
        this.application.getControllerForElementAndIdentifier(
          event.currentTarget,
          'button-spinner'
        );

      // Show the spinner
      if (buttonSpinnerController) {
        buttonSpinnerController.showSpinner();
      }
    }

    // If URL is valid and title is empty, try to fetch title from URL
    if (this.hasTitleTarget && !this.titleTarget.value) {
      this.fetchTitleFromUrl(url);
    }
  }

  isValidUrl(url) {
    try {
      // Try to create a URL object to check validity
      const parsedUrl = new URL(url);
      // Check if protocol is http or https
      return ['http:', 'https:'].includes(parsedUrl.protocol);
    } catch (error) {
      // If URL constructor throws, try to prepend https and check again
      try {
        const urlWithProtocol = `https://${url}`;
        new URL(urlWithProtocol);
        // If successful, update the input with the corrected URL
        this.destinationUrlTarget.value = urlWithProtocol;
        return true;
      } catch (error) {
        return false;
      }
    }
  }

  fetchTitleFromUrl(url) {
    // Create a URL object to ensure it's valid
    let validUrl;
    try {
      validUrl = new URL(url);
    } catch (error) {
      try {
        validUrl = new URL(`https://${url}`);
        // Update the input with the corrected URL
        this.destinationUrlTarget.value = validUrl.toString();
      } catch (error) {
        return;
      }
    }

    // Use a fetch with CORS proxy
    const corsProxyUrl = 'https://api.allorigins.win/get?url=';

    fetch(`${corsProxyUrl}${encodeURIComponent(validUrl.toString())}`)
      .then((response) => {
        if (!response.ok) throw new Error('Failed to fetch URL');
        return response.json();
      })
      .then((data) => {
        if (data && data.contents) {
          // Create a temporary DOM element to parse the HTML
          const doc = new DOMParser().parseFromString(
            data.contents,
            'text/html'
          );

          // Try to extract the title
          const title =
            doc.querySelector('title')?.textContent ||
            doc
              .querySelector("meta[property='og:title']")
              ?.getAttribute('content') ||
            doc
              .querySelector("meta[name='twitter:title']")
              ?.getAttribute('content');

          if (title && !this.titleTarget.value) {
            this.titleTarget.value = title;
          }
        }
      })
      .catch((error) => {
        console.error('Error fetching title:', error);
        // Silently fail - no need to alert the user on this non-critical feature
      });
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
    }
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = '';
    }
  }

  // Called when form is submitted
  validateOnSubmit(event) {
    const url = this.destinationUrlTarget.value.trim();

    if (!url || !this.isValidUrl(url)) {
      event.preventDefault();
      this.showError('We\'ll need a valid URL, like "domain.co/hello"');
      return false;
    }

    return true;
  }
}
